/**
 * @fileoverview Admin Application Shell Component
 * @module ctl-app-shell
 * @version 1.0.0
 * 
 * @description
 * The CtlAppShell component provides a complete application layout structure
 * with responsive navigation, including a topbar, collapsible sidebar, and main
 * content area. It automatically switches to a mobile drawer pattern on smaller
 * screens and includes full keyboard navigation and focus management.
 * 
 * @example
 * <ctl-app-shell variant="hybrid" breakpoint="lg">
 *   <ctl-topbar slot="topbar" title="My App"></ctl-topbar>
 *   <ctl-sidenav slot="sidenav"></ctl-sidenav>
 *   <div slot="content">Main content here</div>
 * </ctl-app-shell>
 * 
 * @fires ctl-toggle - Fired when sidebar collapses/expands or drawer opens/closes
 */

import "./ctl-topbar.js";
import "./ctl-sidenav.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);

    --ctl-shell-radius: var(--ctl-radius-lg, 14px);
    --ctl-shell-shadow: var(--ctl-shadow-md);

    --ctl-sidenav-width: 264px;
    --ctl-sidenav-width-collapsed: 72px;

    --ctl-focus-ring: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    box-sizing: border-box;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
    min-height: 0;
  }

  /* Outer frame (optional visual boundary if used in stories) */
  .frame{
    height: 100%;
    min-height: 0;
    background: var(--ctl-color-bg);
    display:grid;
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
      "topbar"
      "main"
      "footer";
  }

  .topbar{ grid-area: topbar; min-height: 0; }
  .footer{ grid-area: footer; min-height: 0; }

  /* Main area: sidenav + content */
  .main{
    grid-area: main;
    min-height: 0;
    display:grid;
    grid-template-columns: auto 1fr;
    grid-template-areas: "sidenav content";
  }

  /* Variant: topnav only (no sidenav column) */
  :host([variant="topnav"]) .main{
    grid-template-columns: 1fr;
    grid-template-areas: "content";
  }

  /* Sidenav host */
  .sidenavHost{
    grid-area: sidenav;
    min-height: 0;
    border-right: var(--border-width-thin, 1px) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    width: var(--ctl-sidenav-width);
    transition: width var(--transition-fast, 150ms ease-in-out);
    position: relative;
    overflow: hidden;
  }
  :host([collapsed]) .sidenavHost{
    width: var(--ctl-sidenav-width-collapsed);
  }

  /* In hybrid and sidebar variants, we show sidenav. In topnav, hide. */
  :host([variant="topnav"]) .sidenavHost{
    display:none;
  }

  /* Content */
  .content{
    grid-area: content;
    min-height: 0;
    overflow: auto;
    background: color-mix(in srgb, var(--ctl-color-border) 15%, var(--ctl-color-bg));
  }
  .contentInner{
    min-height: 0;
  }

  /* Footer */
  .footer{
    border-top: var(--border-width-thin, 1px) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }
  .footer:empty{ display:none; }

  /* Overlay drawer backdrop (mobile) */
  .drawerBackdrop{
    position: fixed;
    inset: 0;
    background: color-mix(in srgb, var(--color-black) 40%, transparent);
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--ctl-transition-fast);
    z-index: var(--ctl-z-drawer-backdrop);
    backdrop-filter: blur(2px);
  }

  /* Mobile: sidenavHost becomes the sliding drawer panel */
  :host([data-mobile]) .main{
    grid-template-columns: 1fr;
    grid-template-areas: "content";
  }
  :host([data-mobile]) .sidenavHost{
    position: fixed;
    inset: 0 auto 0 0;
    height: 100%;
    width: min(var(--ctl-sidenav-width), var(--ctl-drawer-max-width));
    transform: translateX(-105%);
    transition: transform var(--ctl-transition-normal);
    z-index: var(--ctl-z-drawer);
    box-shadow: var(--ctl-shadow-lg);
    border-right: var(--border-width-thin, 1px) solid var(--ctl-color-border);
    /* Remove the grid-area context when fixed */
    display: flex;
    min-height: 0;
    /* Hidden by default; keyboard and pointer access blocked until open */
    visibility: hidden;
    pointer-events: none;
  }
  :host([data-mobile][data-drawer-open="true"]) .sidenavHost{
    transform: translateX(0);
    visibility: visible;
    pointer-events: auto;
  }
  :host([data-mobile][data-drawer-open="true"]) .drawerBackdrop{
    opacity: 1;
    pointer-events: auto;
  }
  :host([data-mobile][variant="topnav"]) .sidenavHost,
  :host([data-mobile][variant="topnav"]) .drawerBackdrop{
    display:none;
  }

  /* Reduce motion for accessibility */
  @media (prefers-reduced-motion: reduce) {
    .drawerBackdrop,
    .sidenavHost {
      transition: none;
    }
  }

  /* Overlay slot (for dialogs/toasts etc.) */
  .overlay{
    position: relative;
    pointer-events: none; /* child components can override if needed */
    z-index: var(--z-index-tooltip, 1100);
  }
  .overlay ::slotted(*){ pointer-events: auto; }

  /* Hide light DOM for slot templates etc (none here) */
</style>

<div class="frame" part="frame">
  <div class="topbar" part="topbar">
    <slot name="topbar"></slot>
  </div>

  <div class="main" part="main">
    <aside class="sidenavHost" part="sidenavHost" id="sidenavHost" aria-label="Sidebar">
      <slot name="sidenav"></slot>
    </aside>

    <main class="content" part="content">
      <div class="contentInner" part="contentInner">
        <slot name="content"></slot>
      </div>
    </main>
  </div>

  <div class="footer" part="footer">
    <slot name="footer"></slot>
  </div>
</div>

<div class="overlay" part="overlay">
  <slot name="overlay"></slot>
</div>

<div class="drawerBackdrop" part="drawerBackdrop" id="backdrop" aria-hidden="true"></div>
`;

function clampVariant(v) {
  return (v === "sidebar" || v === "topnav") ? v : "hybrid";
}

/**
 * CtlAppShell - Application Shell Component
 * 
 * @class
 * @extends HTMLElement
 * 
 * @attr {string} variant - Layout variant: "hybrid" (default), "sidebar", or "topnav"
 * @attr {boolean} collapsed - Whether the sidebar is collapsed (desktop only)
 * @attr {string} breakpoint - Responsive breakpoint: "sm", "md", "lg" (default), or "xl"
 * 
 * @slot topbar - Top navigation bar (use ctl-topbar)
 * @slot sidenav - Side navigation (use ctl-sidenav)
 * @slot content - Main content area
 * @slot footer - Optional footer content
 * @slot overlay - Overlay content (toasts, dialogs)
 */
export class CtlAppShell extends HTMLElement {
  // Selector for all focusable elements in the drawer
  static FOCUSABLE_SELECTOR = 'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

  static get observedAttributes() {
    return ["variant", "collapsed", "breakpoint"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._backdrop = this.shadowRoot.getElementById("backdrop");
    this._drawer = this.shadowRoot.querySelector('.sidenavHost');

    this._mq = null;
    this._mobile = false;
    this._lastFocusedElement = null;

    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onSlotClickProxy = this._onSlotClickProxy.bind(this);
    this._trapFocus = this._trapFocus.bind(this);
    this._onNavigate = this._onNavigate.bind(this);
    this._onSidenavToggle = this._onSidenavToggle.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("variant")) this.setAttribute("variant", "hybrid");
    if (!this.hasAttribute("breakpoint")) this.setAttribute("breakpoint", "lg");

    this._backdrop.addEventListener("click", this._onBackdropClick);
    window.addEventListener("keydown", this._onKeyDown);

    // Proxy events from ctl-topbar / ctl-sidenav:
    // - if topbar contains a button with [data-shell="toggle-nav"], toggle drawer/collapse.
    // - if sidenav fires ctl-navigate on mobile, close drawer.
    this.addEventListener("click", this._onSlotClickProxy);
    this.addEventListener("ctl-navigate", this._onNavigate);
    this.addEventListener("ctl-toggle", this._onSidenavToggle);

    this._installMediaQuery();
    // Explicitly initialize sidenavHost ARIA state now that _mobile is known.
    // _installMediaQuery only updates ARIA on mobile↔desktop transitions;
    // calling _setDrawerOpen here ensures correct state on the very first render.
    this._setDrawerOpen(false, "init");
    this._sync();
  }

  disconnectedCallback() {
    this._backdrop.removeEventListener("click", this._onBackdropClick);
    window.removeEventListener("keydown", this._onKeyDown);
    this.removeEventListener("click", this._onSlotClickProxy);
    this.removeEventListener("ctl-navigate", this._onNavigate);
    this.removeEventListener("ctl-toggle", this._onSidenavToggle);
    this._uninstallMediaQuery();
  }

  attributeChangedCallback(name) {
    if (name === "breakpoint") this._installMediaQuery();
    this._sync();
  }

  // ---- attributes ----
  get variant() { return clampVariant(this.getAttribute("variant")); }
  set variant(v) { this.setAttribute("variant", clampVariant(v)); }

  get collapsed() { return this.hasAttribute("collapsed"); }
  set collapsed(v) { v ? this.setAttribute("collapsed","") : this.removeAttribute("collapsed"); }

  get breakpoint() { return this.getAttribute("breakpoint") || "lg"; }
  set breakpoint(v) { v ? this.setAttribute("breakpoint", String(v)) : this.removeAttribute("breakpoint"); }

  // ---- responsive ----
  _installMediaQuery() {
    this._uninstallMediaQuery();

    const bp = this.breakpoint;
    const query =
      bp === "sm" ? "(max-width: 640px)" :
      bp === "md" ? "(max-width: 768px)" :
      bp === "xl" ? "(max-width: 1280px)" :
      /* lg default */ "(max-width: 1024px)";

    this._mq = window.matchMedia(query);
    const onChange = () => {
      const nextMobile = !!this._mq.matches;
      if (nextMobile !== this._mobile) {
        this._mobile = nextMobile;
        this.toggleAttribute("data-mobile", this._mobile);

        // When entering mobile, start with drawer closed and mark it inert/hidden.
        if (this._mobile) {
          this._setDrawerOpen(false, "responsive");
          // Ensure closed state ARIA attrs are set on initial mobile entry
          // (the early-return in _setDrawerOpen skips ARIA init when was===false)
          const drawerEl = this._drawer;
          if (drawerEl && !this._drawerOpen()) {
            drawerEl.setAttribute("aria-hidden", "true");
            drawerEl.setAttribute("inert", "");
            drawerEl.removeAttribute("role");
            drawerEl.removeAttribute("aria-modal");
          }
        } else {
          // leaving mobile: ensure drawer closed and reset to desktop ARIA state
          this._setDrawerOpen(false, "responsive");
        }
      }
      this._sync();
    };

    this._mq.addEventListener?.("change", onChange);
    this._mq._ctlHandler = onChange; // fallback for older removal patterns
    onChange();
  }

  _uninstallMediaQuery() {
    if (!this._mq) return;
    const h = this._mq._ctlHandler;
    try { this._mq.removeEventListener?.("change", h); } catch {}
    this._mq = null;
  }

  _sync() {
    // normalize variant
    const v = clampVariant(this.getAttribute("variant"));
    if (this.getAttribute("variant") !== v) this.setAttribute("variant", v);

    // ensure data attrs exist
    this.toggleAttribute("data-mobile", this._mobile);

    // In topnav variant, always close drawer.
    if (this.variant === "topnav") {
      this._setDrawerOpen(false, "responsive");
    }

    // Propagate collapsed state to the slotted sidenav so it renders
    // in icon-only mode and the grid column width resolves correctly.
    const sidenav = this.querySelector('[slot="sidenav"]');
    if (sidenav) {
      const nextCollapsed = !this._mobile && this.collapsed;
      const prevCollapsed = sidenav.hasAttribute("collapsed");

      // Changing the sidenav's "collapsed" attribute will cause it to dispatch
      // a bubbling "ctl-toggle" event. When this change originates from the
      // shell (e.g., desktop collapse/expand), that event would otherwise
      // reach shell-level listeners in addition to the shell's own toggle
      // notification, resulting in duplicate events with different detail
      // payloads. To avoid this, intercept the sidenav-originated event for
      // this programmatic change, stop its propagation, and re-dispatch a
      // single normalized "ctl-toggle" event from the shell.
      if (prevCollapsed !== nextCollapsed) {
        const handleSidenavToggle = (event) => {
          // Only intercept events originating from this sidenav instance.
          if (event.target !== sidenav) return;
          if (typeof event.stopImmediatePropagation === "function") {
            event.stopImmediatePropagation();
          }
          event.stopPropagation();
          sidenav.removeEventListener("ctl-toggle", handleSidenavToggle);
          this.dispatchEvent(
            new CustomEvent("ctl-toggle", {
              bubbles: true,
              composed: true,
              detail: {
                source: "sidenav",
                collapsed: this.collapsed,
                mobile: this._mobile,
              },
            }),
          );
        };

        sidenav.addEventListener("ctl-toggle", handleSidenavToggle);
        sidenav.toggleAttribute("collapsed", nextCollapsed);
      } else {
        // Attribute is already in the desired state; just ensure it's set.
        sidenav.toggleAttribute("collapsed", nextCollapsed);
      }
    }
  }

  // ---- drawer state ----
  _drawerOpen() {
    return this.getAttribute("data-drawer-open") === "true";
  }

  _setDrawerOpen(open, reason) {
    const next = !!open;

    if (!this._mobile) {
      // Desktop uses collapsed docked nav, not drawer. Reset drawer ARIA state.
      this.removeAttribute("data-drawer-open");
      const drawerEl = this._drawer;
      if (drawerEl) {
        drawerEl.removeAttribute("aria-hidden");
        drawerEl.removeAttribute("inert");
        drawerEl.removeAttribute("role");
        drawerEl.removeAttribute("aria-modal");
        drawerEl.setAttribute("aria-label", "Sidebar");
      }
      return;
    }

    if (this.variant === "topnav") {
      this.removeAttribute("data-drawer-open");
      return;
    }

    const was = this._drawerOpen();
    if (next === was) return;

    this.setAttribute("data-drawer-open", next ? "true" : "false");

    // ARIA + inert state management for the mobile drawer
    const drawerEl = this._drawer;
    if (drawerEl) {
      if (next) {
        drawerEl.removeAttribute("aria-hidden");
        drawerEl.removeAttribute("inert");
        drawerEl.setAttribute("role", "dialog");
        drawerEl.setAttribute("aria-modal", "true");
        drawerEl.setAttribute("aria-label", "Navigation");
      } else {
        drawerEl.setAttribute("aria-hidden", "true");
        drawerEl.setAttribute("inert", "");
        drawerEl.removeAttribute("role");
        drawerEl.removeAttribute("aria-modal");
      }
    }

    // Focus management for accessibility
    if (next) {
      // Store the element that triggered the drawer opening
      this._lastFocusedElement = document.activeElement;
      
      // Focus the first focusable element after drawer transition completes
      // Using 200ms: 150ms (--ctl-transition-normal) + 50ms buffer for reliability
      setTimeout(() => {
        const drawerEl = this.shadowRoot.querySelector('.sidenavHost');
        const focusableElements = drawerEl?.querySelectorAll(CtlAppShell.FOCUSABLE_SELECTOR);
        if (focusableElements && focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }, 200);
    } else {
      // Restore focus when drawer closes
      if (this._lastFocusedElement && typeof this._lastFocusedElement.focus === 'function') {
        this._lastFocusedElement.focus();
      }
      this._lastFocusedElement = null;
    }

    // Emit ctl-toggle for visibility changes in mobile drawer too (keeps API simple)
    this.dispatchEvent(new CustomEvent("ctl-toggle", {
      bubbles: true,
      composed: true,
      detail: { collapsed: !next, reason: reason || "user" }
    }));
  }

  // ---- interactions ----
  _onBackdropClick() {
    if (this._mobile) this._setDrawerOpen(false, "user");
  }

  _onKeyDown(e) {
    if (e.key === "Escape" && this._mobile && this._drawerOpen()) {
      e.preventDefault();
      this._setDrawerOpen(false, "user");
    }
    
    // Tab key focus trap when drawer is open
    if (e.key === "Tab" && this._mobile && this._drawerOpen()) {
      this._trapFocus(e);
    }
  }

  _trapFocus(e) {
    const drawer = this._drawer;
    if (!drawer) return;

    const focusableElements = Array.from(
      drawer.querySelectorAll(CtlAppShell.FOCUSABLE_SELECTOR)
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Get the actual focused element, handling shadow DOM
    const getActiveElement = (root = document) => {
      const activeEl = root.activeElement;
      if (activeEl && activeEl.shadowRoot && activeEl.shadowRoot.activeElement) {
        return getActiveElement(activeEl.shadowRoot);
      }
      return activeEl;
    };
    
    const currentFocus = getActiveElement();

    // If shift+tab on first element, focus last
    if (e.shiftKey && currentFocus === firstElement) {
      e.preventDefault();
      lastElement.focus();
    }
    // If tab on last element, focus first
    else if (!e.shiftKey && currentFocus === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }

  _onSlotClickProxy(e) {
    const el = e.target?.closest?.("[data-shell]");
    if (!el) return;

    const action = el.getAttribute("data-shell");
    if (action === "toggle-nav") {
      e.preventDefault();

      if (this._mobile) {
        this._setDrawerOpen(!this._drawerOpen(), "user");
      } else {
        // Desktop: toggle collapsed.
        // Setting this.collapsed triggers attributeChangedCallback → _sync(),
        // which dispatches ctl-toggle via the sidenav intercept when a sidenav
        // is present. Only dispatch here when there is no sidenav.
        this.collapsed = !this.collapsed;
        if (!this.querySelector('[slot="sidenav"]')) {
          this.dispatchEvent(new CustomEvent("ctl-toggle", {
            bubbles: true,
            composed: true,
            // Normalize payload shape with _sync() intercept: { source, collapsed, mobile }
            detail: { source: "shell", collapsed: this.collapsed, mobile: this._mobile }
          }));
        }
      }
    }
  }

  _onNavigate(e) {
    if (this._mobile && this._drawerOpen()) this._setDrawerOpen(false, "user");
  }

  _onSidenavToggle(e) {
    // When the sidenav's own toggle button is used, keep the shell's collapsed
    // state in sync (desktop only; mobile uses drawer, not collapsed).
    const sidenav = this.querySelector('[slot="sidenav"]');
    if (e.target === sidenav && !this._mobile) {
      this.collapsed = e.detail.collapsed;
    }
  }
}

customElements.define("ctl-app-shell", CtlAppShell);
